import { useLayoutEffect, useRef, useState } from "react"
import { motion, useMotionValue, useMotionValueEvent } from "framer-motion"
import styled from "@emotion/styled"

import { useConsole } from "contexts/Console"
import { useNavigation } from "contexts/Navigation"
import { useDictionary } from "contexts/Dictionary"

import getMediaQuery from "css/breakpoints"
import { bold } from "css/text"
import { a11yOutline } from "css/a11y"

import { PARAM_KEY_FILTER, PARAM_KEY_CATEGORY, PARAM_KEY_RANGE, FILTER_SEPERATOR, setSelected, CN_ACTIVE, CN_DISABLED } from "./utils"
import { useSG } from "./context"

const ArrowIcon = styled.span`
  display: block;
  margin-inline-start: 5px;
  background: var(--text);
  height: 1em;
  width: 1em;
  mask-image: url('data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 15 15" aria-hidden="true" focusable="false"><path d="M11.7,7.5l-1.4,1.4l-5.7,5.7l-1.4-1.4l5.7-5.7L3.3,1.9l1.4-1.4l5.7,5.7l0,0L11.7,7.5z"/></svg>');
  --sg-filters-toggle-rotation: 90deg;
  transform: rotate(var(--sg-filters-toggle-rotation));
  transition: transform 0.2s linear;
  align-self: center;

  .sg-filters-active & {
    --sg-filters-toggle-rotation: -90deg;
  }
`

const Backerlay = styled.div`
  display: block;
  width: 100vw;
  height: 100vh;
  background: transparent;
  position: fixed;
  inset: 0;
  pointer-events: none;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s linear, visibility 0s 0.2s;

  &.sg-filters-active {
    pointer-events: all;
    visibility: visible;
    opacity: 1;
    transition: opacity 0.2s linear, visibility 0s;
  }
`

const Toggle = styled(motion.button)`
  --text: rgb(var(--txtcolo));
  position: relative;
  ${bold}
  border: 0;
  background: none;
  color: var(--text);
  text-decoration: none;
  display: flex;
  padding-inline: 1.5rem 1.25rem;
  padding-block: 1rem 0.75rem;
  justify-self: end;
  grid-column: -2;
  grid-row: 1;
  align-self: center;
  transition: color 0.3s;
  background: transparent;
  white-space: nowrap;
  line-height: 1.15;

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      cursor: pointer;
      --text: rgb(var(--green));
    }
  }

  .sg-filters-active & {
    --text: rgb(var(--green));
  }

  ${getMediaQuery("m")} {
    padding-inline: 1rem calc(var(--sg-gap-margin) * 2);
  }
`

const FPanel = styled.div`
  grid-row: 2;
  position: absolute;
  justify-self: end;
  display: flex;
  flex-direction: column;
  visibility: hidden;
  opacity: 0;
  transition: opacity 0.2s linear, visibility 0s 0.2s;
  background: rgb(var(--pure-white));
  border: none;
  border-radius: 0.5rem;
  margin-inline-start: 0;
  margin-inline-end: 1.25rem;
  box-shadow: 4px 10px 20px rgba(0, 0, 0, 0.2);

  width: calc(100vw - (1.25rem + var(--outer-margin) + var(--grid-gap) * 1 + var(--grid-col-unit)));

  ${getMediaQuery("m")} {
    margin-inline-end: var(--sg-gap);
    width: unset;
  }

  & > ul {
    padding: 40px;
  }

  .sg-filters-active & {
    visibility: visible;
    opacity: 1;
    transition: opacity 0.2s linear, visibility 0s;
  }
`

const InputCont = styled.li`
  display: grid;
  grid-template-columns: min-content auto;
  position: relative;
  font-size: 0.875rem;
  gap: 10px;
  align-items: center;

  &:not(:last-of-type) {
    margin-block-end: 12px;
  }

  & label {
    white-space: nowrap;
    font-weight: 400;
    cursor: pointer;
  }

  & input[type="radio"]:checked + label {
    color: rgb(var(--green));
  }

  & input.focus-visible {
    ${a11yOutline}
  }

  & input[type="radio"]::before {
    content: "";
    width: 12px;
    height: 12px;
    border-radius: 50%;
    transform: scale(0);
    transition: 120ms transform ease-in-out;
    background: rgb(var(--green));
  }

  & input[type="radio"]:checked::before {
    transform: scale(1);
  }

  & input[type="radio"]:checked {
    border: 1px solid rgb(var(--green));
  }

  & input[type="radio"] {
    position: relative;
    appearance: none;
    margin: 0;
    font: inherit;
    background: white;
    width: 18px;
    height: 18px;
    border: 1px solid black;
    border-radius: 50%;
    display: grid;
    place-content: center;
  }

  & input:disabled {
    border-color: rgb(var(--grey));
  }
  & input:disabled + label {
    color: rgb(var(--grey));
  }

  /* &.${CN_DISABLED} {
    & input {
      border-color: rgb(var(--grey));
      pointer-events: none;
    }
    & label {
      color: rgb(var(--grey));
      pointer-events: none;
    }
  } */

  /* & input.${CN_DISABLED}, input.${CN_DISABLED} + label {
    color: rgb(var(--grey));
    pointer-events: none;
  } */
`

const SpanValue = styled.span``

function SGFiltersItem({ value, label, update }) {
  const console = useConsole()

  const { avail_filter, filter, filtering } = useSG()

  const [disabledState, setDisabledState] = useState(isDisabled(avail_filter.get()))

  const rfmain = useRef(null)

  const active = useMotionValue(isActive(filter.get()))
  const disabled = useMotionValue(isDisabled(avail_filter.get()))

  function onChange(e) {
    update(value)
  }

  function isActive(v) {
    const all = (v ?? "all").split(FILTER_SEPERATOR).filter(Boolean)
    const index = all.indexOf(value)
    return +(index >= 0)
  }

  function isDisabled(v) {
    const arr = v?.split(FILTER_SEPERATOR)
    const index = arr?.indexOf(value) ?? -1
    const state = index >= 0
    return +!state
  }

  function onActive(v) {
    rfmain.current.classList[v ? "add" : "remove"](CN_ACTIVE)
  }
  useMotionValueEvent(active, "change", onActive)

  function onDisabled(v) {
    setDisabledState(v)
    rfmain.current.classList[v ? "add" : "remove"](CN_DISABLED)
  }
  useMotionValueEvent(disabled, "change", onDisabled)

  function onSelected(v) {
    active.set(isActive(v))
  }
  useMotionValueEvent(filter, "change", onSelected)

  function onAvailable(v) {
    disabled.set(isDisabled(v))
  }
  useMotionValueEvent(avail_filter, "change", onAvailable)

  const cn = [active.get() ? CN_ACTIVE : null, disabled.get() ? CN_DISABLED : null].filter(Boolean).join(" ")

  function onFilter(v) {
    rfmain.current.checked = v === value
  }
  useMotionValueEvent(filter, "change", onFilter)

  function onFiltering(b) {
    if (b && filter.get() === value) {
      rfmain.current.focus()
    }
  }
  useMotionValueEvent(filtering, "change", onFiltering)

  useLayoutEffect(() => {
    rfmain.current.checked = filter.get() === value
  }, [])

  return (
    <InputCont className={cn}>
      <input type='radio' id={`sfilters-radio-${value}`} name='side-filters' ref={rfmain} onChange={onChange} value={value} disabled={disabledState} />
      <label htmlFor={`sfilters-radio-${value}`}>{label}</label>
    </InputCont>
  )
}

export function SGFiltersList({ item, mask, priority, mayReset }) {
  const console = useConsole()
  const navigation = useNavigation()

  const { filtering } = useSG()

  const rfmain = useRef(null)
  const bckeref = useRef(null)

  function update(v) {
    priority.current = "filter"

    mask(1).then(() => {
      navigation.push(
        setSelected(
          [
            {
              k: PARAM_KEY_FILTER,
              v: v,
            },
            {
              k: PARAM_KEY_CATEGORY,
              v: mayReset(v, "filter"),
            },
            {
              k: PARAM_KEY_RANGE,
              v: 0,
            },
          ],
          console
        ),
        { nopageview: true },
        { scroll: false }
      )
    })
  }

  function onClick() {
    filtering.set(1 - filtering.get())
  }
  function onKeyDown(e) {
    if (e.key !== "Escape") return
    if (filtering.get() === 1) filtering.set(1 - filtering.get())
  }

  useLayoutEffect(() => {
    document.addEventListener("keydown", onKeyDown)
    return () => document.removeEventListener("keydown", onKeyDown)
  }, [])

  function onFiltering(v) {
    bckeref.current.classList[v ? "add" : "remove"]("sg-filters-active")
  }
  useMotionValueEvent(filtering, "change", onFiltering)

  return (
    <>
      <Backerlay ref={bckeref} onClick={onClick} />
      <FPanel ref={rfmain} id='filters-panel' role='region' aria-labelledby='filters-btn'>
        <ul>
          {item?.map(v => (
            <SGFiltersItem key={`sg-filter-${v.value}`} {...v} update={update} />
          ))}
        </ul>
      </FPanel>
    </>
  )
}

export default function SGFilters({ item, ...props }) {
  const console = useConsole()
  const dictionary = useDictionary()

  const { filter, filtering } = useSG()

  const [isFiltering, setIsFiltering] = useState(!!filtering.get())

  const colo = useMotionValue("18 119 73")
  /*
    const colo = useMotionValue("33 33 33")
  
    function onFilter(f) {
      colo.set(f === "all" ? "33 33 33" : "18 119 73")
    }
    useMotionValueEvent(filter, "change", onFilter)
  */
  function onClick() {
    filtering.set(1 - filtering.get())
  }

  function onFiltering(f) {
    setIsFiltering(!!f)
  }
  useMotionValueEvent(filtering, "change", onFiltering)

  return (
    <Toggle type='button' style={{ "--txtcolo": colo }} aria-expanded={isFiltering} id='filters-btn' aria-controls='filters-panel' onClick={onClick}>
      <SpanValue id='filter-current-value'>{dictionary.filters()}</SpanValue>
      <ArrowIcon />
    </Toggle>
  )
}
